import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Breadcrumb from '../common/Breadcrumb'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SearchWithFilter from '../common/SearchWithFilter'
import { toast } from '../common/Toast'
import S3Image from '../common/S3Image'
import EmptyPageResults from '../common/EmptyPageResults'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import { COLTENE_BRANCH_UUID } from '../../actions/root'
import { clearCorporateBranchMessages, loadCorporateBranchCities, loadCorporateBranches, loadCorporateBranchStateProvinces } from '../../actions/corporate'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { CompaniesIcon } from '../../icons'
import { keyboardEvents } from '../../utils/keyboardEvents'
import './my-branches.scss'

const orderingFields = [
  {
    field: 'branch',
    order: 'asc',
    translationKey: 'branch_asc',
  },
  {
    field: 'branch',
    order: 'desc',
    translationKey: 'branch_desc',
  },
  {
    field: 'city',
    order: 'asc',
    translationKey: 'city_asc',
  },
  {
    field: 'city',
    order: 'desc',
    translationKey: 'city_desc',
  },
  {
    field: 'stateProvinceRegion',
    order: 'asc',
    translationKey: 'state_asc',
  },
  {
    field: 'stateProvinceRegion',
    order: 'desc',
    translationKey: 'state_desc',
  },
]

const MyBranches = (props) => {
  const { t } = useTranslation()
  const [colteneBranchId, setColteneBranchId, _] = useLocalStorage(COLTENE_BRANCH_UUID)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [stateProvincesFilter, setStateProvincesFilter] = useState([])
  const [citiesFilter, setCitiesFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [showSpinner, setShowSpinner] = useState(null)
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  useEffect(() => {
    if (props.cities.length === 0 && props.companyId) {
      props.actions.loadCorporateBranchCities(props.companyId)
    }

    if (props.stateProvinces.length === 0 && props.companyId) {
      props.actions.loadCorporateBranchStateProvinces(props.companyId)
    }
  }, [props.companyId])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearCorporateBranchMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadBranches(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    if (!props.companyId) {
      return
    }

    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadBranches(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [props.companyId, searchParam, stateProvincesFilter, citiesFilter, orderBy])

  const handleLoadBranches = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadCorporateBranches(props.companyId, queryParams, withReset).then(() => {
      setIsFirstLoad(false)
    })
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setStateProvincesFilter([])
    setCitiesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (stateProvincesFilter.length > 0) {
      queryParams = { ...queryParams, stateProvinceRegion: stateProvincesFilter }
    }

    if (citiesFilter.length > 0) {
      queryParams = { ...queryParams, city: citiesFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    setOrderBy(orderingFields.find((o) => o.field === sortOption.field && o.order === sortOption.order))
  }

  const handleNavigation = (e, branchUuid) => {
    e.preventDefault()

    setColteneBranchId(branchUuid, false)
    window.location.replace('/')
  }

  return (
    <div className="my-branches-wrapper">
      <Breadcrumb path={window.location.pathname} />
      <DashboardHeader headerText={t('nav.my_branches')} />
      <div className="my-branches-header">
        <SearchWithFilter
          onSearch={(e) => handleSearch(e)}
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={stateProvincesFilter.length > 0 || citiesFilter.length > 0}
          counterBadge={stateProvincesFilter.length + citiesFilter.length}
        />
        <div className="dropdown-area">
          <div className="sorting-label">{t('sort_by')}</div>
          <Dropdown
            className="dropdown-input"
            placeholder={t('select_ordering')}
            options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
            initialSelectedKey={orderBy?.translationKey}
            onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
          />
        </div>
      </div>
      <div className="branch-container">
        {props.branches.map((branch, index) => (
          <div
            key={branch.branchUuid}
            className="wrapper-item"
            onClick={(e) => handleNavigation(e, branch.branchUuid)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => e.key === keyboardEvents.ENTER && handleNavigation(e, branch.branchUuid)}
          >
            <S3Image imageKey={branch.imageUrl} fallbackIcon={<CompaniesIcon />} />
            {/* <div className="branch-image-wrapper">
              {branch.imageUrl?.length > 0 ? <img alt={branch.branchName} src={branch.imageUrl} /> : <CompaniesIcon />}
            </div> */}
            <div className="branch-info-wrapper">
              <div className="branch-name">{branch.branchName}</div>
              <div className="branch-location">{`${branch.city}, ${branch.stateProvinceRegion}`}</div>
              <div className="branch-data">{`${branch.members} ${t('members_single_multi')}`}</div>
              <div className="branch-data">{`${branch.totalDevices} ${t('products_single_many')}`}</div>
              <div className="branch-action">
                <SimpleButton className="action-button">{t('view_branch')}</SimpleButton>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!showSpinner && !isFirstLoad && props.branches.length === 0 && <EmptyPageResults title={t('organization_context.no-branches-found')} />}
      {canLoadMore && (
        <div className="load-more-wrapper d-flex flex-justify-center">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('city'),
              type: FilterType.multiSelect,
              dataset: props.cities,
              input: citiesFilter,
              output: (filterCities) => {
                setCitiesFilter(filterCities)
              },
            },
            {
              name: t('state_province'),
              type: FilterType.multiSelect,
              dataset: props.stateProvinces,
              input: stateProvincesFilter,
              output: (filterStateProvinces) => {
                setStateProvincesFilter(filterStateProvinces)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ userPermissions, userCompany, corporate }) {
  return {
    userPermissions,
    branches: corporate?.branches || [],
    isLoading: corporate?.isLoading,
    canLoadMore: corporate?.canLoadMore,
    errorMessage: corporate?.errorMessage,
    companyId: userCompany?.companyId,
    cities: corporate?.branchCities || [],
    stateProvinces: corporate?.branchStateProvinces || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCorporateBranches,
        loadCorporateBranchCities,
        loadCorporateBranchStateProvinces,
        clearCorporateBranchMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(MyBranches)

